/**
 * Lightweight logger utility for production logging cleanup.
 *
 * - dev: all levels print to browser console
 * - production: only error() prints (message string only) + auto-POSTs to /track/event
 */

const isDev = import.meta.env.DEV;

interface LoggerConfig {
  apiBaseUrl: string;
  visitorId: string;
  sessionId: string;
  shopDomain: string;
  deviceType: string;
}

interface QueuedError {
  message: string;
  context?: Record<string, unknown>;
}

let config: LoggerConfig | null = null;
const errorQueue: QueuedError[] = [];

function postError(message: string, context?: Record<string, unknown>): void {
  if (!config) {
    errorQueue.push({ message, context });
    return;
  }

  const body = JSON.stringify({
    visitorId: config.visitorId,
    sessionId: config.sessionId,
    shopDomain: config.shopDomain,
    deviceType: config.deviceType,
    event: 'client_error',
    properties: {
      message,
      ...context,
    },
    timestamp: new Date().toISOString(),
  });

  fetch(`${config.apiBaseUrl}/track/event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
  }).catch(() => {});
}

function flushQueue(): void {
  while (errorQueue.length > 0) {
    const { message, context } = errorQueue.shift()!;
    postError(message, context);
  }
}

export const logger = {
  debug(message: string, ...args: unknown[]): void {
    if (isDev) {
      console.log(message, ...args);
    }
  },

  info(message: string, ...args: unknown[]): void {
    if (isDev) {
      console.log(message, ...args);
    }
  },

  warn(message: string, ...args: unknown[]): void {
    if (isDev) {
      console.warn(message, ...args);
    }
  },

  error(message: string, context?: Record<string, unknown>): void {
    if (isDev) {
      if (context) {
        console.error(message, context);
      } else {
        console.error(message);
      }
    } else {
      console.error(message);
    }
    postError(message, context);
  },

  configure(cfg: LoggerConfig): void {
    config = cfg;
    flushQueue();
  },
};
